import CardsHelper from '../../../Utility/CardsHelper';
import { CardType } from '../../../Prototype/Level/Card/CardType';
import { Random } from '../../../Utility/Random';
import CardStateHelper, { getCardsState } from '../CardStateHelper';
import { CardTask } from '../CardTasks';
import { DEFAULT_CARD_HEIGHT, DEFAULT_CARD_WIDTH } from '../../../Prototype/Level/Card/ICardData';
import { ICardStateData } from '../ICardState';
import { ICardModifierData } from '../../../Prototype/Level/Card/ICardModifierData';
import { initialCardStateData } from '../initialCardStateData';
import { IGameState } from 'State/Game/IGameState';
import TimerModifierBehavior from './TimerModifierBehavior';
import { isLevelFailed } from 'State/Level/LevelStateHelper';

export default class BombModifierBehavior extends TimerModifierBehavior {
    public onCardMatched(state: IGameState, matchedCardID: string): IGameState {
        if (!this.validateUpdate(getCardsState(state)) || matchedCardID === this.cardID) return state;

        return this.setTimer(state, this.modifier.properties.timer - 1);
    }

    public onCardMatchedLate(state: IGameState): IGameState {
        // This is to verify that the explode animation and board update only happens once
        if (this.modifier.properties.exploded || CardStateHelper.isCardDiscarded(state, this.cardID)) return state;

        if (this.isTimerFinished(state)) {
            return this.explode(state, this.cardID);
        }

        return state;
    }

    public onUndoUsed(state: IGameState, prevCardState: ICardStateData, prevModifier: ICardModifierData): IGameState {
        // TODO: Update validateUpdate to use IGameState
        if (!this.validateUpdate(state.levelState.cardsState)) return state;

        state = this.setTimer(state, prevModifier.properties.timer);

        return state;
    }

    /**
     * Prepares explosion by creating cards, adding animation tasks, and updating board
     * @param cardsState
     * @param cardID
     * @returns cardsState
     */
    protected explode(state: IGameState, cardID: string): IGameState {
        const newCards = [];
        let cardsState = state.levelState.cardsState;

        // Discard card
        state = CardStateHelper.setCardDiscarded(state, this.cardID);
        cardsState = getCardsState(state);

        // Add 3-5 random cards positions on top of other in-play cards.
        const numberOfCardsAdded = Random.int(3, 5);
        for (let i = 0; i < numberOfCardsAdded; i++) {
            // Default new card data (in case a target card to overlay is not found).
            const newCardData = {
                x: Random.value(-500, 500),
                y: Random.value(200, 500),
                rotation: 0,
                depth: i,
            };

            // Attempt to find a random, face-up, not obscured, in-play card and add a new card in front.
            const targetCards = CardStateHelper.getActiveCards(state).filter((cardID: string) => {
                const behavior = CardStateHelper.getCardBehavior(cardsState, cardID);
                return behavior.isFaceUp(cardsState) && !behavior.isObscured(cardsState);
            });

            const targetCardID = targetCards[Random.int(0, targetCards.length - 1)];

            // If a target card is found set relative position/rotation/depth.
            if (targetCardID) {
                const cardData = cardsState.cards[targetCardID];
                newCardData.x = cardData.x;
                newCardData.y = cardData.y; // + 25;
                newCardData.rotation = cardData.rotation + Random.value(-Math.PI / 8, Math.PI / 8);
                newCardData.depth = cardData.depth + 1;

                cardsState = CardStateHelper.updateCard(cardsState, targetCardID, {
                    faceUp: false,
                });

                if (i === 0) {
                    cardsState = CardStateHelper.setStateTaskData(cardsState, {
                        task: CardTask.FLIP,
                        cards: [targetCardID],
                        faceUp: false,
                    });
                }
            }

            const newCardID = `card_${Random.ID()}`;

            cardsState = CardStateHelper.addCard(cardsState, {
                ...initialCardStateData,
                id: newCardID,
                type: CardType.value,
                random: true,
                value: CardsHelper.instance.draw(),
                faceUp: true,
                sequence: 0,
                modifiers: [],
                attempts: 0,
                x: newCardData.x,
                y: newCardData.y,
                rotation: newCardData.rotation,
                depth: newCardData.depth,
                width: DEFAULT_CARD_WIDTH,
                height: DEFAULT_CARD_HEIGHT,
            });

            newCards.push(newCardID);

            cardsState = CardStateHelper.setCardInPlay(cardsState, newCardID);
        }

        if (!isLevelFailed(state)) {
            // Set action data to trigger bomb explode task.
            cardsState = CardStateHelper.setStateTaskData(cardsState, {
                task: CardTask.BOMB,
                cards: [cardID, ...newCards],
            });
        }

        // Clear undo history when a bomb explodes.
        cardsState = CardStateHelper.clearUndoHistory(cardsState);

        // This is to ensure that the animation / board update happens on late update
        cardsState = this.updateModifier(cardsState, this.cardID, {
            properties: {
                ...this.modifier.properties,
                exploded: true,
            },
        });

        cardsState = CardStateHelper.checkCardsObscured(cardsState);

        state = CardStateHelper.updateGameState(state, cardsState);

        return state;
    }
}
